import { FilePreview } from "@/components/agent-fs/FilePreview";
import { FileTree } from "@/components/agent-fs/FileTree";
import { buildTreeFromManifest } from "@/components/agent-fs/FilesystemPanel";
import { RunTimeline } from "@/components/agent-fs/RunTimeline";
import { SessionCompare } from "@/components/agent-fs/SessionCompare";
import type { FileTier } from "@/components/agent-fs/TierBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { useAgentStore } from "@/lib/agent";
import { formatDate } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { ArrowLeft, FolderOpen, MessageSquare } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/assistant/sessions/$id")({
	component: AssistantSessionDetail,
});

const aiClient = createClient(AIService, transport);

function AssistantSessionDetail() {
	const { t } = useTranslation("agent-fs");
	const { id } = useParams({
		from: "/assistant/sessions/$id",
	});
	const navigate = useNavigate();
	const fsPreviewPath = useAgentStore((s) => s.fsPreviewPath);

	const { data: session } = useQuery({
		queryKey: ["agent-session", id],
		queryFn: async () => {
			return await aiClient.getAgentSession({ id });
		},
		enabled: !!id,
	});

	const { data: files } = useQuery({
		queryKey: ["agent-session-files", id],
		queryFn: async () => {
			const res = await aiClient.listAgentSessionFiles({
				sessionId: id,
				tier: "",
			});
			return res.files.map((f) => ({
				path: f.path,
				tier: f.tier,
				size: Number(f.size),
				modified: f.modifiedAt ? f.modifiedAt.toDate().toISOString() : "",
				sha256: f.sha256,
			}));
		},
		enabled: !!id,
	});

	const selectedFile = files?.find((f) => f.path === fsPreviewPath);
	const treeRoots = useMemo(() => buildTreeFromManifest(files ?? []), [files]);

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Button
					variant="ghost"
					size="icon"
					className="size-8"
					onClick={() => navigate({ to: "/assistant" })}
				>
					<ArrowLeft className="size-4" />
				</Button>
				<FolderOpen className="size-5 text-blue-500" />
				<div className="flex-1 min-w-0">
					<h2 className="text-sm font-medium truncate">
						{session?.label || t("session_label", { id: id.slice(0, 8) })}
					</h2>
					<p className="text-xs text-muted-foreground">
						{t("files_count", { count: session?.fileCount ?? 0 })} ·{" "}
						{session?.status === "active"
							? t("status_active")
							: (session?.status ?? "")}
						{session?.createdAt &&
							` · ${t("created_at", { date: formatDate(session.createdAt.toDate(), "d MMM yyyy HH:mm") })}`}
					</p>
				</div>
				{session?.conversationId && (
					<Button
						variant="outline"
						size="sm"
						onClick={() =>
							navigate({
								to: "/assistant/$threadId",
								params: { threadId: session.conversationId },
							})
						}
					>
						<MessageSquare className="size-3.5 mr-1.5" />
						{t("back_to_dialog")}
					</Button>
				)}
			</div>

			{/* Tabs */}
			<Tabs defaultValue="files">
				<TabsList>
					<TabsTrigger value="files">{t("tab_files")}</TabsTrigger>
					<TabsTrigger value="runs">{t("tab_runs")}</TabsTrigger>
					<TabsTrigger value="compare">{t("tab_compare")}</TabsTrigger>
				</TabsList>

				<TabsContent value="files">
					<div className="flex rounded-lg border overflow-hidden h-[500px]">
						<div className="w-64 shrink-0 border-r overflow-y-auto">
							<FileTree roots={treeRoots} />
						</div>
						<div className="flex-1 overflow-hidden">
							{fsPreviewPath ? (
								<FilePreview
									sessionId={id}
									path={fsPreviewPath}
									tier={selectedFile?.tier as FileTier | undefined}
									size={selectedFile?.size}
									sha256={selectedFile?.sha256}
								/>
							) : (
								<div className="flex items-center justify-center h-full text-xs text-muted-foreground">
									{t("select_file")}
								</div>
							)}
						</div>
					</div>
				</TabsContent>

				<TabsContent value="runs">
					<RunTimeline sessionId={id} />
				</TabsContent>

				<TabsContent value="compare">
					<SessionCompare currentSessionId={id} />
				</TabsContent>
			</Tabs>
		</div>
	);
}
